import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { GmailOAuthService, GmailProblem } from '../../../core/services/gmail-oauth.service';
import { TranslationService } from '../../../core/services/i18n/translation.service';
import { NotificationService } from '../../../core/services/notification.service';

/**
 * "Vuelva a conectar su Gmail" for the signed-in user whose mailbox brings no mail in (card 1055 A11): on live
 * six staff mailboxes stopped for weeks and only their owner can connect them again. The button starts the same
 * Google sign-in as the login page, with Google's consent screen so a refused mailbox gets new access.
 */
@Component({
  selector: 'app-gmail-banner',
  standalone: true,
  templateUrl: './gmail-banner.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GmailBannerComponent implements OnInit {
  private gmail = inject(GmailOAuthService);
  private notifications = inject(NotificationService);
  protected readonly translationService = inject(TranslationService);

  problem = signal<GmailProblem | null>(null);
  /** Closed for this visit: it comes back on the next page load while the mailbox is still stopped */
  dismissed = signal(false);
  connecting = signal(false);

  ngOnInit(): void {
    this.gmail.getConnectionStatus().subscribe({
      next: (status) => this.problem.set(status.problem ?? null),
      // A hint only: with no answer there is nothing to say
      error: () => this.problem.set(null),
    });
  }

  reconnect(): void {
    if (this.connecting()) return;
    this.connecting.set(true);
    this.gmail.initiateOAuth(true).subscribe({
      next: (response) => {
        try {
          sessionStorage.setItem('oauth_state', response.state);
        } catch {
          // storage blocked: the server checks the state on its own
        }
        this.goTo(response.authorization_url);
      },
      error: (err: unknown) => {
        this.connecting.set(false);
        this.notifications.httpError('common.error', err, 'gmailHealth.reconnectFailed');
      },
    });
  }

  /** Leaves the app for Google's sign-in page (its own method, so a spec can stop it) */
  goTo(url: string): void {
    window.location.href = url;
  }
}
